import { Autocomplete, Button, Column, ErrorBox, Modal, Row, TextInput } from "../../../../widget";
import { useInviteDeveloperModal } from "./useInviteDeveloperModal";

interface InviteDeveloperModalProps {
  onClose: () => void;
  onInvitationSent?: (shouldPollForInvitation?: boolean) => void;
  projectUuid: string;
  show: boolean;
}

export const InviteDeveloperModal = ({
  onClose,
  onInvitationSent,
  projectUuid,
  show,
}: InviteDeveloperModalProps): JSX.Element => {
  const {
    developerOptions,
    email,
    errors,
    firstName,
    isInviteDisabled,
    isSubmitting,
    lastName,
    onCancel,
    onDeveloperChange,
    onDeveloperTypeSearch,
    onEmailChange,
    onFirstNameChange,
    onLastNameChange,
    onOrganisationNameChange,
    onSendInvitation,
    organisationName,
    selectedDeveloperKey,
    shouldShowContactFields,
    shouldShowOrganisationNameField,
  } = useInviteDeveloperModal({
    onClose,
    onInvitationSent,
    projectUuid,
    show,
  });

  return (
    <Modal show={show} title="Invite developer" onClose={onCancel} className="InviteDeveloperModal">
      <form onSubmit={onSendInvitation}>
        {errors && errors.length > 0 && (
          <Row spacingV="ml" justify="center">
            <Column span={8}>
              <ErrorBox
                title="Please solve the below issues before sending the invitation"
                text={errors.map((error) => error.message)}
              />
            </Column>
          </Row>
        )}
        <Row spacingV="2xl" justify="center">
          <Column span={10}>
            <p className="body1 InviteDeveloperModalDescription">
              Select a developer to add to the project. This will mean the organisation has access to the project in the
              Kana Earth platform and can upload data against it. If they don&apos;t already use Kana Earth, you can
              invite them to the application.
            </p>
          </Column>
        </Row>
        <Row spacingV="ll" justify="center">
          <Column span={6} className="InviteDeveloperModalFieldColumn">
            <Autocomplete
              label="Developer"
              placeholder="Start typing to find the developer"
              data={developerOptions}
              initialKey={selectedDeveloperKey}
              onSelect={onDeveloperChange}
              onTypeSearch={onDeveloperTypeSearch}
              corners="rounded"
            />
          </Column>
        </Row>
        {shouldShowOrganisationNameField && (
          <Row spacingV="ll" justify="center">
            <Column span={6} className="InviteDeveloperModalFieldColumn">
              <TextInput
                label="Organisation name"
                name="organisationName"
                value={organisationName}
                onChange={onOrganisationNameChange}
              />
            </Column>
          </Row>
        )}
        {shouldShowContactFields && (
          <>
            <Row spacingV="ll" justify="center">
              <Column span={6} className="InviteDeveloperModalFieldColumn">
                <TextInput type="email" label="Email address" name="email" value={email} onChange={onEmailChange} />
              </Column>
            </Row>
            <Row spacingV="ll" justify="center">
              <Column span={6} className="InviteDeveloperModalFieldColumn">
                <TextInput label="First name" name="firstName" value={firstName} onChange={onFirstNameChange} />
              </Column>
            </Row>
            <Row spacingV="ll" justify="center">
              <Column span={6} className="InviteDeveloperModalFieldColumn">
                <TextInput label="Last name" name="lastName" value={lastName} onChange={onLastNameChange} />
              </Column>
            </Row>
          </>
        )}
        <Row spacingV="2xl" justify="center" align="center" className="InviteDeveloperModalActionsRow">
          <Column span={3} className="InviteDeveloperModalPrimaryAction">
            <Button
              type="submit"
              text="Invite"
              fullWidth
              corners="rounded"
              disabled={isInviteDisabled}
              isLoading={isSubmitting}
            />
          </Column>
          <Column span={2} className="InviteDeveloperModalSecondaryAction">
            <Button
              text="Cancel"
              variant="secondary"
              hasNoPadding
              minWidth={false}
              onClick={onCancel}
              disabled={isSubmitting}
            />
          </Column>
        </Row>
      </form>
    </Modal>
  );
};
